/**
 * Auth wiring.
 *
 * Two modes coexist:
 * - **local** — backend `LocalAuthAdapter` issues a session cookie. The chat
 *   panel's existing `/login` form drives this. No external IdP.
 * - **cognito** — Cognito Hosted UI handles sign-in. `react-oidc-context`
 *   manages the OAuth code + PKCE flow, stores the tokens, and refreshes them.
 *   This file:
 *     - exposes `<AuthProvider>` that does nothing in local mode and wraps with
 *       `react-oidc-context` in cognito mode,
 *     - exposes `getAccessToken()` that the API layer calls before every
 *       request to attach a Bearer header,
 *     - exposes a small `useAuth()` shim with `mode`, `isAuthenticated`,
 *       `email`, `signIn`, `signOut`.
 *
 * `api.ts` doesn't care which mode is on — it just calls `getAccessToken()`.
 */

import type { ReactNode } from "react";
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from "react-oidc-context";

export type AuthMode = "local" | "cognito";

const env = import.meta.env;
export const AUTH_MODE: AuthMode =
  env.VITE_AUTH_PROVIDER === "cognito" ? "cognito" : "local";

const COGNITO_REGION = env.VITE_COGNITO_REGION ?? "us-east-1";
const COGNITO_USER_POOL_ID = env.VITE_COGNITO_USER_POOL_ID ?? "";
const COGNITO_CLIENT_ID = env.VITE_COGNITO_CLIENT_ID ?? "";
const COGNITO_DOMAIN = env.VITE_COGNITO_DOMAIN ?? "";

const SPA_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "http://localhost:5173";

const cognitoConfig =
  AUTH_MODE === "cognito"
    ? {
        authority: `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/${COGNITO_USER_POOL_ID}`,
        client_id: COGNITO_CLIENT_ID,
        redirect_uri: `${SPA_ORIGIN}/auth/callback`,
        response_type: "code",
        scope: "openid email profile",
        // Cognito doesn't advertise an end_session_endpoint on its OIDC
        // discovery doc, so react-oidc-context can't sign-out automatically.
        // We trigger the Cognito logout URL ourselves in `signOut()`.
        automaticSilentRenew: true,
        loadUserInfo: false,
      }
    : null;

/**
 * Top-level provider. In local mode this is a no-op pass-through.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (!cognitoConfig) return <>{children}</>;
  return <OidcAuthProvider {...cognitoConfig}>{children}</OidcAuthProvider>;
}

// Module-level holder so non-React code (api.ts) can read the current token
// without crawling the component tree. `AuthTokenSync` (below) keeps it fresh.
let _accessToken: string | null = null;

export function getAccessToken(): string | null {
  return _accessToken;
}

export function _setAccessToken(token: string | null) {
  _accessToken = token;
}

// Hydrate from localStorage SYNCHRONOUSLY at module load so the very first
// API call (the auth guard's /auth/me) already has a Bearer header. Without
// this, the guard would flicker to "unauthenticated" on every full reload.
if (AUTH_MODE === "cognito" && typeof window !== "undefined") {
  try {
    const raw = window.localStorage.getItem("dravian.cognito.tokens");
    if (raw) {
      const parsed = JSON.parse(raw) as {
        access_token: string;
        expires_at: number;
      };
      if (parsed && Date.now() < parsed.expires_at) {
        _accessToken = parsed.access_token;
      }
    }
  } catch {
    /* swallow — corrupted storage just means unauthenticated */
  }
}

/**
 * Tiny invisible component mounted under AuthProvider. Two responsibilities:
 *
 * - **Hosted UI flow**: mirror the OIDC user's access_token into the
 *   module-level holder.
 * - **In-app SRP flow**: hydrate the cached token from localStorage on first
 *   render, then schedule a periodic refresh so the SPA doesn't 401 mid-session.
 */
export function AuthTokenSync() {
  if (AUTH_MODE !== "cognito") return null;
  return <CognitoTokenMirror />;
}

function CognitoTokenMirror() {
  // Hosted UI path (when used) keeps the token fresh via react-oidc-context.
  const auth = useOidcAuth();
  const oidcToken = auth.user?.access_token ?? null;

  // Hydrate from SRP-cached tokens on first mount.
  // Lazy import to avoid a build-time circular dependency between auth.tsx and cognito-srp.ts.
  if (typeof window !== "undefined") {
    if (!(window as unknown as { __dravianHydrated__?: boolean }).__dravianHydrated__) {
      (window as unknown as { __dravianHydrated__?: boolean }).__dravianHydrated__ = true;
      import("@/lib/cognito-srp").then((m) => {
        const hydrated = m.hydrateFromStorage();
        if (hydrated) {
          // Schedule background refresh every 90s.
          setInterval(() => m.srpRefreshIfPossible().catch(() => undefined), 90_000);
        }
      });
    }
  }

  // If the OIDC flow is in use this overrides anything the SRP flow set.
  if (oidcToken) _setAccessToken(oidcToken);
  return null;
}

// ---------- useAuth shim ----------

export interface AuthShim {
  mode: AuthMode;
  isLoading: boolean;
  isAuthenticated: boolean;
  email: string | null;
  name: string | null;
  signIn: () => void;
  signOut: () => void;
  error: string | null;
}

export function useAuth(): AuthShim {
  if (AUTH_MODE !== "cognito") {
    return {
      mode: "local",
      isLoading: false,
      isAuthenticated: false,
      email: null,
      name: null,
      signIn: () => {
        window.location.href = "/login";
      },
      signOut: async () => {
        // Local mode: just hit the existing /auth/logout endpoint.
        await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
        window.location.href = "/login";
      },
      error: null,
    };
  }
  // Cognito mode — call the hook
  return useCognitoShim();
}

function useCognitoShim(): AuthShim {
  const auth = useOidcAuth();
  return {
    mode: "cognito",
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
    email: (auth.user?.profile?.email as string | undefined) ?? null,
    name: (auth.user?.profile?.name as string | undefined) ?? null,
    signIn: () => auth.signinRedirect(),
    signOut: () => {
      // 1. Clear local oidc-client state.
      auth.removeUser();
      // 2. Tell Cognito to end its session and redirect back to /.
      const logoutUrl =
        `https://${COGNITO_DOMAIN}/logout` +
        `?client_id=${COGNITO_CLIENT_ID}` +
        `&logout_uri=${encodeURIComponent(`${SPA_ORIGIN}/`)}`;
      window.location.href = logoutUrl;
    },
    error: auth.error?.message ?? null,
  };
}
